import React, { Component } from 'react';
import PropTypes from 'prop-types';

import DownloadButtonWrapper from '../shared/buttons/download_button_wrapper';
import DownloadButton from 'js/canonical_cards/work_card/download_button';
import DownloadButtonSearchRedesign from 'js/canonical_cards/work_card/download_button_search_redesign';
import LibraryButtonWrapper from '../shared/buttons/library_button_wrapper';
import LibraryButton from './library_button';
import LibraryButtonSearchRedesign from './library_button_search_redesign';
import BulkDownloadButton from './bulk_download_button';
import MoreActionsButton from './more_actions_button';
import { isCoronavirusWork } from 'js/coronavirus/coronavirus_research_detector';

/* Renders a row of buttons for a WorkCard, including:
 *   Download (with post-download modal)
 *   Bulk Download (only for premium users)
 *   Add/Remove from Library (with toast)
 *   A 'More Actions' button, containing:
 *     A share button, which opens a share modal with a people search bar and share suggestions.
 *     A report button, which opens a report survey. Delivers survey results and creates a RelatedWorkRejection on submission.
 *     A 'not interested' button, which opens a survey. Delivers survey results and creates a RelatedWorkRejection on submission.
 */
class WorkActions extends Component {
  static defaultProps = {
    loggedIn: true,
    showSearchRedesign: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      fetchingBookmark: false, // true when a bookmark request is pending
    };
  }

  render() {
    const {
      loggedIn,
      work,
      source,
      reportWork,
      addToLibrary,
      removeFromLibrary,
      showBulkDownloadButton,
      showBulkDownloadModal,
      loadWorkShareSuggestions,
      enableAddToWorkCollection,
      addToWorkCollectionModalProps,
      showRemoveFromWorkCollection,
      removeFromWorkCollection,
      showSearchRedesign,
    } = this.props;
    const downloadable = work.downloadableAttachments[0] != null;
    const premium = this.premium();
    const isOpenalexWork = work.contentType === 'OpenalexWork';

    return (
      <div className="work-card--action-row-wrapper">
        <div className="work-card--actions-wrapper">
          {isOpenalexWork && (
            <DownloadButton
              styleAsDownloaded={false}
              isExternalSource={true}
              onDownloadClick={() =>
                window.open(work.internalUrl, '_blank', 'noreferrer')
              }
            />
          )}
          {downloadable && (
            <DownloadButtonWrapper
              bulkDownloadFilterCounts={work.filterCounts}
              work={work}
              source={source}
              premium={premium}
              autoBookmark={loggedIn}
              showAutoBookmarkToast={loggedIn}
              showBulkDownloadUpsell={showBulkDownloadModal}
              // if we're showing the BD modal - let it handle rendering the ReaderMessageForm
              // premium users should always see it
              showReaderMessageModal={
                loggedIn && (!showBulkDownloadModal || premium)
              }
              styleAsDownloaded={true}
              addToLibrary={addToLibrary}
              removeFromLibrary={removeFromLibrary}
              gateDownload={true}
            >
              {showSearchRedesign ? (
                <DownloadButtonSearchRedesign />
              ) : (
                <DownloadButton />
              )}
            </DownloadButtonWrapper>
          )}
          {downloadable && loggedIn && (premium || showBulkDownloadButton) && (
            <BulkDownloadButton
              work={work}
              source={source}
              premium={premium}
              showSearchRedesign={showSearchRedesign}
            />
          )}
          {loggedIn ? (
            <LibraryButtonWrapper
              work={work}
              source={source}
              addToLibrary={addToLibrary}
              removeFromLibrary={removeFromLibrary}
            >
              {showSearchRedesign ? (
                <LibraryButtonSearchRedesign />
              ) : (
                <LibraryButton />
              )}
            </LibraryButtonWrapper>
          ) : null}
          {loggedIn && !showSearchRedesign && !isOpenalexWork && (
            <MoreActionsButton
              work={work}
              reportWork={reportWork}
              loadWorkShareSuggestions={loadWorkShareSuggestions}
              source={source}
              enableAddToWorkCollection={enableAddToWorkCollection}
              addToWorkCollectionModalProps={addToWorkCollectionModalProps}
            />
          )}
        </div>
        {showRemoveFromWorkCollection && (
          <div className="work-card--remove-from-list-wrapper">
            <div
              className="work-card--remove-from-list-button"
              onClick={removeFromWorkCollection}
            >
              <div className="work-card--remove-from-list-icon">
                <i className="fa fa-minus-circle"></i>
              </div>
              <div className="work-card--remove-from-list-text">
                Remove from List
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  premium = () => this.props.premium || isCoronavirusWork(this.props.work);
}

WorkActions.propTypes = {
  premium: PropTypes.bool.isRequired,
  work: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    isBookmarked: PropTypes.bool.isRequired,
    downloadableAttachments: PropTypes.arrayOf(
      PropTypes.shape({
        downloadUrl: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        scribdThumbnailUrl: PropTypes.string,
      }),
    ).isRequired,
    authors: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
        displayName: PropTypes.string.isRequired,
        photo: PropTypes.string,
      }),
    ).isRequired,
    metadata: PropTypes.shape({
      abstract: PropTypes.string,
      publicationDate: PropTypes.object,
      publicationName: PropTypes.string,
    }),
    researchInterests: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    bulkDownloadPurchased: PropTypes.bool,
    alreadySharedUserIds: PropTypes.array,
    shareSuggestions: PropTypes.array,
    // Used for bulk download button upsell
    filterCounts: PropTypes.object,
  }).isRequired,
  reportWork: PropTypes.func.isRequired,
  loadWorkShareSuggestions: PropTypes.func.isRequired,
  addToLibrary: PropTypes.func.isRequired,
  removeFromLibrary: PropTypes.func.isRequired,
  // the source location of this work card, for data tracking
  source: PropTypes.oneOf([
    'news-feed',
    'search-results',
    'library',
    'coronavirus_page',
    'rankings',
  ]).isRequired,
  // determines whether to show BD modal to free users on download click
  showBulkDownloadModal: PropTypes.bool.isRequired,
  // determines whether to show BD button to free users as upsell
  showBulkDownloadButton: PropTypes.bool.isRequired,
  loggedIn: PropTypes.bool,
  // determines whether to show an "add to list" prompt in the more actions menu
  enableAddToWorkCollection: PropTypes.bool,
  addToWorkCollectionModalProps: PropTypes.shape({
    startAddingItemToCollection: PropTypes.func,
    workCollectionsWithWorks: PropTypes.objectOf(
      PropTypes.shape({
        id: PropTypes.number.is_required,
        name: PropTypes.string.is_required,
        work_count: PropTypes.number,
        works: PropTypes.array,
      }),
    ),
  }),
  showRemoveFromWorkCollection: PropTypes.bool,
  removeFromWorkCollection: PropTypes.func, // Removes this work from the List it's displayed in
  showSearchRedesign: PropTypes.bool,
};
export default WorkActions;
